/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
// libraries
import React, { useMemo, useState, useEffect } from 'react';
// material
import { SectionGroupV2 } from 'material/expandablesection/v2'
import Space from 'material/space/space'
import { CheckboxField, SingleSelectDropDown } from '@insticator/insticator-ui'
import PreviewConfig from '../../previewconfig/previewconfig'
import { ReactComponent as CaretDown } from 'assets/icons/caret-down.svg'
// styles
import injectSheet from 'react-jss'
import styles from './sorting_styles'

const sortOptionList = [
  { name: 'Newest', option: 'Newest', key: 'useNewestCommentsSort', value: 'NEWEST'},
  { name: 'Oldest', option: 'Oldest', key: 'useOldestCommentsSort', value: 'OLDEST'},
  { name: 'Most popular', option: 'Most popular', key: 'useMostPopularThreadsSort', value: 'MOST_POPULAR'},
  { name: 'Most replies', option: 'Most replies', key: 'useMostRepliedThreadsSort', value: 'MOST_REPLIED'},
  { name: 'My comments', option: 'My comments', key: 'useMyCommentsSort', value: 'MY_COMMENTS'},
]

const defaultSortOption = {
  'NEWEST': sortOptionList[0],
  'OLDEST': sortOptionList[1],
  'MOST_POPULAR': sortOptionList[2],
  'MOST_REPLIED': sortOptionList[3],
  'MY_COMMENTS': sortOptionList[4]
}

/* ========== ~~~~~~~~~~ COMMENTING CONFIG V2: GENERAL SECTION : SORTING ~~~~~~~~~~ ========== */
const Sorting = injectSheet(styles)((props) => {

  const { defaultSort, useNewestCommentsSort, useMostPopularThreadsSort, useOldestCommentsSort, useMostRepliedThreadsSort, useMyCommentsSort, onChange } = props

  const [defaultSorting, setDefaultSorting] = useState(defaultSortOption[defaultSort || 'NEWEST'])

  useEffect(() => {
    setDefaultSorting(defaultSortOption[defaultSort])
  }, [defaultSort])

  const handleSelectDefaultSorting = opt => {
    setDefaultSorting(opt)
    onChange('defaultSort', opt.value)
  }

  const availableSortingOptions = useMemo(() => {
    const options = []
    if(useNewestCommentsSort) options.push({ name: 'Newest', option: 'Newest', key: 'useNewestCommentsSort', value: 'NEWEST'})
    if(useOldestCommentsSort) options.push({ name: 'Oldest', option: 'Oldest', key: 'useOldestCommentsSort', value: 'OLDEST'})
    if(useMostPopularThreadsSort) options.push({ name: 'Most popular', option: 'Most popular', key: 'useMostPopularThreadsSort', value: 'MOST_POPULAR'})
    if(useMostRepliedThreadsSort) options.push({ name: 'Most replies', option: 'Most replies', key: 'useMostRepliedThreadsSort', value: 'MOST_REPLIED'})
    return options
  }, [useNewestCommentsSort, useOldestCommentsSort, useMostPopularThreadsSort, useMostRepliedThreadsSort])

  const sortingPreviewList = useMemo(() => {
    return (
      <>
        <span className='selected'>{defaultSorting.option}<CaretDown className='caret' fill='#FFF' /></span>
        {useNewestCommentsSort && defaultSorting.value !== 'NEWEST' && <span>Newest</span>}
        {useOldestCommentsSort && defaultSorting.value !== 'OLDEST' && <span>Oldest</span>}
        {useMostPopularThreadsSort && defaultSorting.value !== 'MOST_POPULAR' && <span>Most popular</span>}
        {useMostRepliedThreadsSort && defaultSorting.value !== 'MOST_REPLIED' && <span>Most replies</span>}
        {useMyCommentsSort && <span>My comments</span>}
      </>
    )
  }, [defaultSorting, useNewestCommentsSort, useOldestCommentsSort, useMostPopularThreadsSort, useMostRepliedThreadsSort, useMyCommentsSort])

  return (
    <SectionGroupV2 
      title={'Sorting'} 
      klass={`sorting ${props.classes.sorting}`}
      description={'Select how users can sort comments and what the default sort behavior is.'}
      indented
    >
      <Space direction={'column'} gap={24}>
        <Space direction={'column'}>
          <CheckboxField 
            type={`checkbox`} 
            name={`change_permissions`} 
            value={`NEWEST`} 
            checked={useNewestCommentsSort}
            handleChange={() => onChange('useNewestCommentsSort', !useNewestCommentsSort)}
            disabled={(!useMostPopularThreadsSort && !useOldestCommentsSort && !useMostPopularThreadsSort) || defaultSorting.key === 'useNewestCommentsSort'}
            valid
            label={`Newest`} 
          />
          <CheckboxField 
            type={`checkbox`} 
            name={`change_permissions`} 
            value={`OLDEST`} 
            checked={useOldestCommentsSort}
            handleChange={() => onChange('useOldestCommentsSort', !useOldestCommentsSort)}
            disabled={(!useMostPopularThreadsSort && !useNewestCommentsSort && !useMostPopularThreadsSort) || defaultSorting.key === 'useOldestCommentsSort'}
            valid
            label={`Oldest`} 
          />
          <CheckboxField 
            type={`checkbox`} 
            name={`change_permissions`} 
            value={`MOST_POPULAR`} 
            checked={useMostPopularThreadsSort}
            handleChange={() => onChange('useMostPopularThreadsSort', !useMostPopularThreadsSort)}
            disabled={(!useNewestCommentsSort && !useOldestCommentsSort && !useMostPopularThreadsSort) || defaultSorting.key === 'useMostPopularThreadsSort'}
            valid
            label={`Most popular`} 
          />
          <CheckboxField 
            type={`checkbox`} 
            name={`change_permissions`} 
            value={`MOST_REPLIED`} 
            checked={useMostRepliedThreadsSort}
            handleChange={() => onChange('useMostRepliedThreadsSort', !useMostRepliedThreadsSort)}
            disabled={(!useNewestCommentsSort && !useOldestCommentsSort && !useMostPopularThreadsSort) || defaultSorting.key === 'useMostRepliedThreadsSort'}
            valid
            label={`Most replies`} 
          />
          <CheckboxField 
            type={`checkbox`} 
            name={`change_permissions`} 
            value={`MY_COMMENTS`} 
            checked={useMyCommentsSort}
            handleChange={() => onChange('useMyComments', !useMyCommentsSort)}
            disabled={true}
            valid
            label={`My comments`} 
          />
        </Space>
        <SingleSelectDropDown 
          name={defaultSorting.name} 
          label={'Default option'}
          searchable={false} 
          onSelect={handleSelectDefaultSorting}
          items={availableSortingOptions}
        />
      </Space>
      <PreviewConfig>
        <Space klass={'sortingpreview'}>
          {sortingPreviewList}
        </Space>
      </PreviewConfig>
    </SectionGroupV2>
  )
})

/* ++++++++++ --------------- EXPORTS --------------- ++++++++++ */
export default Sorting;